import React, { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import trash from "../../svgs/Trash.svg";
import Icon from '../Icon/Icon';

const LEGACY_CLOUDFRONT_HOST = 'dj4ao97lyirny.cloudfront.net';
const LEGACY_V0_SEGMENT      = '/v0/';

/**
 * Normalize a card link to the current media base URL.
 *
 * Existing PRICING_ANALYSIS records in the database store the full legacy
 * CloudFront URL as `card.link` (e.g. "https://dj4ao97...cloudfront.net/.../v0/PRICING_ANALYSIS/page.jpg").
 * New uploads via AttachmentController already store a full GCS public URL directly.
 *
 * This function re-bases any legacy CloudFront URL onto arc.path.media so that
 * both old DB records and new uploads render correctly without touching the database.
 */
function resolveCardLink(link) {
    if (!link) return link;
    if (!link.includes(LEGACY_CLOUDFRONT_HOST)) return link;

    const v0Pos = link.indexOf(LEGACY_V0_SEGMENT);
    if (v0Pos === -1) return link;

    // arc.path.media is injected at runtime via window.arc from config.production.json.
    // It currently resolves to "https://storage.googleapis.com/{BUCKET}/".
    return arc.path.media + link.slice(v0Pos + LEGACY_V0_SEGMENT.length);
}

export const Card = ({ card, index, moveCard, onDrop, remove, type, standardPages, readOnly = false }) => {
  const ref = useRef(null)
  const [{ handlerId, isOver, canDrop }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }
    },
    hover(item, monitor) {
      if (!ref.current || readOnly) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const rect = ref.current.getBoundingClientRect();
      const midX = (rect.right - rect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverX = clientOffset.x - rect.left;

      if (dragIndex < hoverIndex && hoverX < midX) return;
      if (dragIndex > hoverIndex && hoverX > midX) return;

      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => ({ index }),
    canDrag: () => !readOnly,
    end: () => { if (onDrop) onDrop(); },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  drag(drop(ref))
  const pageNum = index + (standardPages || (type === 'MT' ? 4 : 3)) + 1;
  const dropTarget = isOver && canDrop;

  return (
    <div
      ref={ref}
      className={`DragableCard_card${isDragging ? ' dragging' : ''}${dropTarget ? ' drop-target' : ''}`}
      data-handler-id={handlerId}
    >
      <div className="DragableCard_card_header">
        <span className="DragableCard_card_page">Page {pageNum}</span>
        {remove && (
          <button className="DragableCard_card_remove" onClick={() => remove(card)} title="Remove page">
            <Icon svg={trash} />
          </button>
        )}
      </div>
      <div className="DragableCard_card_image">
        <img src={resolveCardLink(card.link)} alt={`Page ${pageNum}`} draggable={false} />
      </div>
    </div>
  )
}
